import { createFileRoute, redirect } from '@tanstack/react-router'
import type { HrSettingsTab } from '@/features/hr-settings/hr-settings-screen'
import { HrSettingsScreen } from '@/features/hr-settings/hr-settings-screen'
import type { LeaveTypeStatus } from '@/features/hr-settings/leave-types-panel'
import type { WorkSiteEmployeeOptionsFilters } from '@/features/hr-settings/work-site-assignment-api'
import { ViolationTypesPanel } from '@/features/hr-violations/violation-types-panel'
import { ApprovalPathsPanel } from '@/features/workflow-settings/approval-paths-panel'
import { hasGrant } from '@/lib/me'

type HrSettingsSearch = {
  leaveTypeStatus?: LeaveTypeStatus
  tab?: HrSettingsTab
  showHidden?: boolean
  workSiteEmployeeSearch?: string
  workSiteEmployeeBranchId?: string
  workSiteEmployeeDepartmentId?: string
  workSiteEmployeePage?: number
}

export const Route = createFileRoute('/_authenticated/hr/settings')({
  beforeLoad: ({ context, search }) => {
    if (
      !hasGrant(context.queryClient, 'employee.view') &&
      !(search.tab === 'conduct' && hasGrant(context.queryClient, 'violation.edit')) &&
      // ADR-0034: an approval-path editor need not see employee records.
      !(
        search.tab === 'approvals' &&
        (hasGrant(context.queryClient, 'hr_workflow.view') ||
          hasGrant(context.queryClient, 'workflow.view'))
      )
    ) {
      throw redirect({ to: '/' })
    }
  },
  validateSearch: (search: Record<string, unknown>): HrSettingsSearch => ({
    leaveTypeStatus:
      search.leaveTypeStatus === 'all' ||
      search.leaveTypeStatus === 'active' ||
      search.leaveTypeStatus === 'inactive'
        ? search.leaveTypeStatus
        : undefined,
    tab:
      search.tab === 'leave' ||
      search.tab === 'work' ||
      search.tab === 'payroll' ||
      search.tab === 'conduct' ||
      search.tab === 'categories' ||
      search.tab === 'approvals'
        ? search.tab
        : 'leave',
    showHidden: search.showHidden === true ? true : undefined,
    workSiteEmployeeSearch:
      typeof search.workSiteEmployeeSearch === 'string' && search.workSiteEmployeeSearch !== ''
        ? search.workSiteEmployeeSearch
        : undefined,
    workSiteEmployeeBranchId:
      typeof search.workSiteEmployeeBranchId === 'string' && search.workSiteEmployeeBranchId !== ''
        ? search.workSiteEmployeeBranchId
        : undefined,
    workSiteEmployeeDepartmentId:
      typeof search.workSiteEmployeeDepartmentId === 'string' &&
      search.workSiteEmployeeDepartmentId !== ''
        ? search.workSiteEmployeeDepartmentId
        : undefined,
    workSiteEmployeePage:
      typeof search.workSiteEmployeePage === 'number' &&
      Number.isInteger(search.workSiteEmployeePage) &&
      search.workSiteEmployeePage > 1
        ? search.workSiteEmployeePage
        : undefined,
  }),
  component: HrSettingsRoute,
})

function HrSettingsRoute() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const workSiteEmployeePickerFilters: WorkSiteEmployeeOptionsFilters = {
    search: search.workSiteEmployeeSearch ?? '',
    branchId: search.workSiteEmployeeBranchId ?? '',
    departmentId: search.workSiteEmployeeDepartmentId ?? '',
    page: search.workSiteEmployeePage ?? 1,
  }
  const updateWorkSiteEmployeePickerFilters = (filters: WorkSiteEmployeeOptionsFilters) =>
    void navigate({
      search: {
        ...search,
        workSiteEmployeeSearch: filters.search || undefined,
        workSiteEmployeeBranchId: filters.branchId || undefined,
        workSiteEmployeeDepartmentId: filters.departmentId || undefined,
        workSiteEmployeePage: filters.page > 1 ? filters.page : undefined,
      },
      replace: true,
    })

  return (
    <HrSettingsScreen
      conductPanel={ViolationTypesPanel}
      approvalPathsPanel={ApprovalPathsPanel}
      conductShowHidden={search.showHidden}
      onConductShowHiddenChange={(showHidden) =>
        void navigate({
          search: { ...search, showHidden: showHidden ? true : undefined },
          replace: true,
        })
      }
      leaveTypeStatus={search.leaveTypeStatus}
      onLeaveTypeStatusChange={(leaveTypeStatus) =>
        void navigate({
          search: {
            ...search,
            leaveTypeStatus: leaveTypeStatus === 'active' ? undefined : leaveTypeStatus,
          },
          replace: true,
        })
      }
      tab={search.tab}
      onTabChange={(tab) =>
        void navigate({
          search: { ...search, tab: tab === 'leave' ? undefined : tab },
          replace: true,
        })
      }
      workSiteEmployeePickerFilters={workSiteEmployeePickerFilters}
      onWorkSiteEmployeePickerFiltersChange={updateWorkSiteEmployeePickerFilters}
    />
  )
}
